import { useState } from "react";
import { toast } from "sonner";
import { GitMerge } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EmployeePicker } from "@/components/people/EmployeePicker";

function Preview({ emp, into }) {
  const { data } = useGet(`/employees/${emp.id}/merge-preview?into=${into.id}`);
  if (!data) return <p className="text-sm text-stone-500">Checking records…</p>;
  return (
    <div className="rounded-lg border bg-[#FBF9F5] p-3 text-sm space-y-1" data-testid="merge-preview">
      <div><b>Keep:</b> {data.keep.name} · {data.keep.role || "no role"} · {data.keep.status}{data.keep_login ? " · has app login" : ""}</div>
      <div><b>Retire:</b> {data.remove.name} · {data.remove.role || "no role"} (marked Merged, not deleted)</div>
      <div>Moves {data.sales_records} sales record(s), {data.tip_shifts} tip shift(s), {data.notes} note(s), {data.goals} goal(s), {data.identities} linked source identity(ies).</div>
      {data.same_day_records > 0 && <div className="text-amber-800">{data.same_day_records} same-day record(s) already exist on the kept profile — they'll be kept as separate entries and flagged for review, never added together.</div>}
      {data.remove_login && data.keep_login && <div className="text-amber-800">Both profiles have app logins — logins are left unchanged.</div>}
    </div>
  );
}

export function MergeButton({ emp, onDone }) {
  const [open, setOpen] = useState(false);
  const [into, setInto] = useState(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${emp.id}/merge`, { into_id: into.id });
      toast.success(`Merged — ${data.moved.tip_shifts} tip shift(s) and ${data.moved.employee_sales} sales record(s) moved`);
      setOpen(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => { setOpen(true); setInto(null); setOk(false); }} className="p-2 text-stone-400 hover:text-stone-900" aria-label="Merge duplicate profile" title="Merge duplicate profile" data-testid={`employee-merge-${emp.id}`}><GitMerge className="h-4 w-4" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto space-y-3" data-testid="merge-dialog">
          <DialogTitle className="font-display text-2xl">Merge duplicate profile</DialogTitle>
          <DialogDescription>Only for two directory profiles that are the same person. To connect imported sales or tip-out names, use “Link imported data” instead.</DialogDescription>
          <div className="text-sm">Retiring: <b>{emp.name}</b>{emp.role ? ` · ${emp.role}` : ""}</div>
          <div className="text-sm font-medium">Keep which profile?</div>
          <EmployeePicker value={into?.id} onChange={(e) => { setInto(e); setOk(false); }} exclude={[emp.id]} testid="merge-into" />
          {into && <Preview emp={emp} into={into} />}
          {into && <label className="flex gap-2 text-sm"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="merge-ack" />I confirm these two profiles are the same person.</label>}
          <Button className="h-12 w-full" disabled={!into || !ok || busy} onClick={go} data-testid="merge-confirm">{busy ? "Merging…" : "Merge records"}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
